import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, Check, Copy, CurrencyBtc, Hourglass, Warning, Star, ArrowLeft } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";

// Crypto payment constants
const WALLET_ADDRESS = "0xb43900bc28dbc2397c2dabb1ca9c42b9c59965b1";
const CRYPTO_PRICES = {
  monthly: 10,
  yearly: 89,
  lifetime: 394
};
const PLAN_LABELS = {
  monthly: "Monthly",
  yearly: "Yearly",
  lifetime: "Lifetime"
};

// Upgrade pricing
const UPGRADE_PRICE = 79; // Discounted price for Monthly -> Yearly upgrade

export default function CryptoPaymentModal({ 
  isOpen, 
  onClose, 
  onPaymentSubmitted,
  existingPendingPayment = null,
  initialPlan = null,
  // Upgrade context props
  isUpgrade = false,
  upgradePrice = null,
  // Back navigation prop
  onBack = null
}) {
  const [selectedPlan, setSelectedPlan] = useState(initialPlan || "monthly");
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bonusGranted, setBonusGranted] = useState(false);
  const [copied, setCopied] = useState(false);

  // Update selected plan when initialPlan prop changes
  useEffect(() => {
    if (initialPlan && !showConfirmation) {
      setSelectedPlan(initialPlan);
    }
  }, [initialPlan, isOpen]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Only reset confirmation state when modal is closed
      // This prevents the modal from showing confirmation on next open
      setShowConfirmation(false);
      setBonusGranted(false);
    }
  }, [isOpen]);

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(WALLET_ADDRESS);
      setCopied(true);
      toast.success("Wallet address copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy address");
    }
  };

  const handlePaymentComplete = async () => {
    setSubmitting(true);
    try {
      const response = await api.post('/payments/crypto', {
        plan_type: selectedPlan,
        is_upgrade: isUpgrade,
        upgrade_price: isUpgrade ? (upgradePrice || UPGRADE_PRICE) : null
      });
      
      // Check if bonus was granted with this submission
      const wasGranted = response.data.bonus_granted === true;
      setBonusGranted(wasGranted);
      
      // Show confirmation state
      setShowConfirmation(true);
      
      // Notify parent
      if (onPaymentSubmitted) {
        onPaymentSubmitted(response.data);
      }
      
      toast.success("Payment submitted for review!");
    } catch (error) {
      console.error("Error submitting crypto payment:", error);
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    if (!existingPendingPayment) {
      setShowConfirmation(false);
    }
    onClose();
  };

  if (!isOpen) return null;

  // Calculate amount - use upgrade price if this is an upgrade
  const amount = isUpgrade ? (upgradePrice || UPGRADE_PRICE) : CRYPTO_PRICES[selectedPlan];
  const originalPrice = CRYPTO_PRICES.yearly;

  // Confirmation state - shown after submission or when there's a pending payment
  if (showConfirmation) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
        data-testid="crypto-confirmation-overlay"
      >
        <div 
          className="bg-[#121212] border border-white/10 rounded-sm p-8 w-full max-w-md mx-4 relative"
          data-testid="crypto-confirmation-modal"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Hourglass size={32} weight="fill" className="text-yellow-400" />
            </div>
            <h2 className="text-2xl font-black font-heading tracking-tight mb-2">Payment Submitted</h2>
            <p className="text-white/60">
              Your payment is now pending review. You will be notified as soon as your Pro access is activated.
            </p>
          </div>

          {/* Bonus trades message - only show if bonus was just granted */}
          {bonusGranted && (
            <div className="bg-[#00E676]/10 border border-[#00E676]/30 rounded-sm p-4 mb-4">
              <p className="text-[#00E676] text-sm font-medium text-center">
                While your payment is under review, you now have <span className="font-bold">10 extra trades</span> so you can continue using the platform.
              </p>
              <p className="text-[#00E676]/70 text-xs text-center mt-2">
                This benefit is available only once per account.
              </p>
            </div>
          )}

          {/* Payment details */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 mb-6">
            <div className="flex justify-between items-center text-sm">
              <span className="text-white/50">Plan</span>
              <span className="text-white font-medium">
                Pro {PLAN_LABELS[selectedPlan] || PLAN_LABELS.monthly}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-white/50">Amount</span>
              <span className="text-white font-medium">{amount} USDT</span>
            </div>
          </div>

          {/* Button */}
          <Button
            onClick={handleClose}
            className="w-full bg-white text-black font-bold hover:bg-gray-200"
            data-testid="crypto-confirmation-close"
          >
            Got it
          </Button>
        </div>
      </div>
    );
  }

  // Main payment modal
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      data-testid="crypto-modal-overlay"
    >
      <div 
        className="bg-[#121212] border border-white/10 rounded-sm p-6 w-full max-w-lg mx-4 relative max-h-[90vh] overflow-y-auto"
        data-testid="crypto-modal"
      >
        {/* Back button - only show if onBack is provided */}
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 text-white/40 hover:text-white transition-colors"
            data-testid="crypto-modal-back"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
        )}

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
          data-testid="crypto-modal-close"
        >
          <X size={20} weight="bold" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#F0B90B]/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <CurrencyBtc size={28} weight="fill" className="text-[#F0B90B]" />
          </div>
          <h2 className="text-2xl font-black font-heading tracking-tight mb-1">
            {isUpgrade ? "Upgrade to Yearly" : "Pay with Crypto"}
          </h2>
          <p className="text-white/50 text-sm">USDT on BEP20 Network</p>
          
          {/* Upgrade discount banner */}
          {isUpgrade && (
            <div className="mt-4 bg-[#00E676]/10 border border-[#00E676]/30 rounded-sm p-3">
              <div className="flex items-center justify-center gap-2 text-[#00E676]">
                <Star size={16} weight="fill" />
                <span className="font-semibold text-sm">Exclusive Upgrade Discount</span>
              </div>
              <p className="text-white/70 text-xs mt-1">
                Save ${originalPrice - amount} — get 1 month free!
              </p>
            </div>
          )}
        </div>

        {/* Plan Selection - Hidden for upgrades (plan is pre-selected) */}
        {!isUpgrade && (
          <div className="mb-6">
            <label className="text-xs text-white/50 mb-2 block font-mono">SELECT PLAN</label>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={() => setSelectedPlan("monthly")}
                data-testid="crypto-plan-monthly"
                className={`p-3 sm:p-4 rounded-sm border transition-all ${
                  selectedPlan === "monthly"
                    ? "bg-[#F0B90B]/10 border-[#F0B90B] text-white"
                    : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                }`}
              >
                <div className="text-lg font-bold">{CRYPTO_PRICES.monthly} USDT</div>
                <div className="text-xs text-white/50">Monthly</div>
              </button>
              <button
                onClick={() => setSelectedPlan("yearly")}
                data-testid="crypto-plan-yearly"
                className={`p-3 sm:p-4 rounded-sm border transition-all relative ${
                  selectedPlan === "yearly"
                    ? "bg-[#F0B90B]/10 border-[#F0B90B] text-white"
                    : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                }`}
              >
                <div className="absolute -top-2 right-2 bg-[#00E676] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
                  SAVE 40%
                </div>
                <div className="text-lg font-bold">{CRYPTO_PRICES.yearly} USDT</div>
                <div className="text-xs text-white/50">Yearly</div>
              </button>
              <button
                onClick={() => setSelectedPlan("lifetime")}
                data-testid="crypto-plan-lifetime"
                className={`p-3 sm:p-4 rounded-sm border transition-all ${
                  selectedPlan === "lifetime"
                    ? "bg-[#F0B90B]/10 border-[#F0B90B] text-white"
                    : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                }`}
              >
                <div className="text-lg font-bold">{CRYPTO_PRICES.lifetime} USDT</div>
                <div className="text-xs text-white/50">Lifetime</div>
              </button>
            </div>
          </div>
        )}
        
        {/* Upgrade plan summary - Only shown for upgrades */}
        {isUpgrade && (
          <div className="mb-6">
            <label className="text-xs text-white/50 mb-2 block font-mono">UPGRADE PLAN</label>
            <div className="bg-[#F0B90B]/10 border border-[#F0B90B] rounded-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-semibold flex items-center gap-2">
                    <Star size={16} weight="fill" className="text-[#FFD700]" />
                    Pro Yearly
                  </div>
                  <div className="text-white/50 text-xs mt-0.5">12 months of Pro access</div>
                </div>
                <div className="text-right">
                  <div className="text-[#F0B90B] font-bold text-lg">{amount} USDT</div>
                  <div className="text-white/40 text-xs line-through">{originalPrice} USDT</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Instructions */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 mb-4">
          <div className="flex flex-col items-center mb-4">
            {/* QR Code */}
            <div className="bg-white p-3 rounded-sm mb-3">
              <QRCodeSVG
                value={WALLET_ADDRESS}
                size={160}
                level="M"
                data-testid="crypto-qr-code"
              />
            </div>
            <p className="text-xs text-white/40 text-center">Scan to get wallet address</p>
          </div>

          {/* Wallet Details */}
          <div className="space-y-3">
            <div>
              <label className="text-xs text-white/40 mb-1 block">Token</label>
              <div className="bg-[#121212] border border-white/10 rounded-sm px-3 py-2 text-white font-mono text-sm" data-testid="crypto-token">
                USDT
              </div>
            </div>
            
            <div>
              <label className="text-xs text-white/40 mb-1 block">Network</label>
              <div className="bg-[#121212] border border-white/10 rounded-sm px-3 py-2 text-white font-mono text-sm" data-testid="crypto-network">
                BEP20 (BNB Smart Chain)
              </div>
            </div>
            
            <div>
              <label className="text-xs text-white/40 mb-1 block">Amount</label>
              <div className="bg-[#121212] border border-[#F0B90B]/30 rounded-sm px-3 py-2 text-[#F0B90B] font-mono text-sm font-bold" data-testid="crypto-amount">
                {amount} USDT
              </div>
            </div>

            <div>
              <label className="text-xs text-white/40 mb-1 block">Wallet Address</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-[#121212] border border-white/10 rounded-sm px-3 py-2 text-white font-mono text-xs break-all" data-testid="crypto-wallet-address">
                  {WALLET_ADDRESS}
                </div>
                <Button
                  onClick={handleCopyAddress}
                  variant="outline"
                  size="sm"
                  className="border-white/10 hover:bg-white/5 shrink-0"
                  data-testid="crypto-copy-address"
                >
                  {copied ? (
                    <Check size={16} className="text-[#00E676]" />
                  ) : (
                    <Copy size={16} />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-3 mb-6">
          <div className="flex items-start gap-2">
            <Warning size={18} className="text-red-400 mt-0.5 shrink-0" />
            <div className="text-xs text-red-300/90">
              <p className="font-semibold mb-1">Important</p>
              <p>Only send <span className="font-bold">USDT</span> via <span className="font-bold">BEP20</span> to this address.</p>
              <p className="mt-1 text-red-300/70">Sending through the wrong network may result in loss of funds.</p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          onClick={handlePaymentComplete}
          disabled={submitting}
          className="w-full bg-[#F0B90B] text-black font-bold hover:bg-[#D4A50A] py-3"
          data-testid="crypto-complete-payment"
        >
          {submitting ? (
            <>
              <span className="animate-spin mr-2">&#9696;</span>
              Submitting...
            </>
          ) : (
            <>
              <Check size={18} className="mr-2" />
              I completed the payment
            </>
          )}
        </Button>

        {/* Footer note */}
        <p className="text-center text-white/30 text-xs mt-4">
          After submission, your payment will be manually verified and your account upgraded.
        </p>
      </div>
    </div>
  );
}
